import classNames from "classnames";
import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import SvgArrowDropDown from "../components/icons/ArrowDropDown";
import SvgNavigateBefore from "../components/icons/NavigateBefore";
import SvgNavigateNext from "../components/icons/NagivateNext";
import Icon from "../components/ui/Icon";
import { useI18n } from "../hooks/useTranslations";
import { isMobile } from "../types/utils";
import styles from "./DatePicker.module.css";
import { compareDateInput, formatMonthYear, getMonthNames, toDateInput } from "./datePickerUtils";
import { useFloatingPanel } from "./useFloatingPanel";

export type MonthYearPickerProps = {
  className?: string;
  value: string;
  onChange: (value: string) => void;
  clearable?: boolean;
  disabledMonths?: string[] | ((value: string) => boolean);
  label?: string;
  maxDate?: string;
  minDate?: string;
  placeholder?: string;
};

const YEAR_RANGE_PAST = 100;
const YEAR_RANGE_FUTURE = 10;

function currentMonthValue() {
  return toDateInput(new Date()).slice(0, 7);
}

export function MonthYearPicker({
  className,
  value,
  onChange,
  clearable = false,
  disabledMonths,
  label,
  maxDate,
  minDate,
  placeholder,
}: MonthYearPickerProps) {
  const i18n = useI18n();
  const labels = i18n.t.common.monthYearPicker;
  const locale = i18n.language;

  const [isOpen, setIsOpen] = useState(false);
  const [viewYear, setViewYear] = useState(() => Number((value || currentMonthValue()).slice(0, 4)));
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerId = useId();

  const panelStyle = useFloatingPanel(isOpen, () => setIsOpen(false), containerRef, panelRef);

  useEffect(() => {
    if (isOpen) {
      setViewYear(Number((value || currentMonthValue()).slice(0, 4)));
    }
  }, [isOpen, value]);

  const today = currentMonthValue();
  const currentYear = new Date().getFullYear();
  const monthNames = getMonthNames(locale, "short");
  const months = Array.from({ length: 12 }, (_, index) => `${viewYear}-${String(index + 1).padStart(2, "0")}`);
  const years = Array.from(
    { length: YEAR_RANGE_PAST + YEAR_RANGE_FUTURE + 1 },
    (_, index) => currentYear - YEAR_RANGE_PAST + index,
  );
  const isMonthDisabled = (month: string) => {
    if (minDate && compareDateInput(month, minDate) < 0) {
      return true;
    }

    if (maxDate && compareDateInput(month, maxDate) > 0) {
      return true;
    }

    if (Array.isArray(disabledMonths)) {
      return disabledMonths.includes(month);
    }

    if (typeof disabledMonths === "function") {
      return disabledMonths(month);
    }

    return false;
  };

  if (isMobile) {
    return (
      <div className={classNames(styles.wrap, className)}>
        {label && (
          <label className={styles.label} htmlFor={triggerId}>
            {label}
          </label>
        )}

        <div className={styles.mobileRow}>
          <input
            id={triggerId}
            type="month"
            className={styles.mobileInput}
            value={value}
            min={minDate}
            max={maxDate}
            onChange={(event) => onChange(event.target.value)}
          />
          {clearable && value && (
            <button type="button" className={styles.clearButton} onClick={() => onChange("")}>
              {labels.clearButton}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={classNames(styles.wrap, className)} ref={containerRef}>
      {label && (
        <label className={styles.label} htmlFor={triggerId}>
          {label}
        </label>
      )}

      <button id={triggerId} type="button" className={styles.trigger} onClick={() => setIsOpen((open) => !open)}>
        <span className={classNames(styles.value, !value && styles.placeholder)}>
          {value ? formatMonthYear(value, locale) : (placeholder ?? labels.placeholder)}
        </span>
        <Icon glyph={SvgArrowDropDown} className={classNames(styles.caret, isOpen && styles.caretOpen)} />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={panelRef}
            className={styles.panel}
            style={
              panelStyle ?? {
                position: "fixed",
                top: 0,
                left: 0,
                visibility: "hidden",
              }
            }
          >
            <div className={styles.panelHeader}>
              <button
                type="button"
                className={styles.navButton}
                onClick={() => setViewYear((current) => current - 1)}
                aria-label={labels.previousYear}
              >
                <Icon glyph={SvgNavigateBefore} className={styles.navIcon} />
              </button>

              <div className={styles.monthYearSelect}>
                <select
                  aria-label={labels.selectYear}
                  className={styles.select}
                  value={String(viewYear)}
                  onChange={(event) => setViewYear(Number(event.target.value))}
                >
                  {years.map((year) => (
                    <option key={year} value={String(year)}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                className={styles.navButton}
                onClick={() => setViewYear((current) => current + 1)}
                aria-label={labels.nextYear}
              >
                <Icon glyph={SvgNavigateNext} className={styles.navIcon} />
              </button>
            </div>

            <div className={styles.months}>
              {months.map((month, index) => {
                const disabled = isMonthDisabled(month);

                return (
                  <button
                    key={month}
                    type="button"
                    disabled={disabled}
                    className={classNames(styles.month, {
                      [styles.monthDisabled]: disabled,
                      [styles.monthToday]: month === today,
                      [styles.monthSelected]: month === value,
                    })}
                    onClick={() => {
                      onChange(month);
                      setIsOpen(false);
                    }}
                  >
                    {monthNames[index]}
                  </button>
                );
              })}
            </div>

            {clearable && value && (
              <button
                type="button"
                className={styles.clearButton}
                onClick={() => {
                  onChange("");
                  setIsOpen(false);
                }}
              >
                {labels.clearButton}
              </button>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}

export default MonthYearPicker;
